import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Clock, Users, Pencil } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";

// Convert an ISO datetime string to the value accepted by <input type="datetime-local">
function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function DailyView({ currentUser }) {
  const [date, setDate] = useState(todayIso());
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    (async () => {
      const [a, u] = await Promise.all([
        api.get("/attendance/all", { params: { date_str: date } }),
        api.get("/users"),
      ]);
      setItems(a.data || []);
      setUsers(u.data || []);
    })();
  }, [date]);

  const indexByUser = useMemo(() => Object.fromEntries(items.map((it) => [it.user_id, it])), [items]);
  const allUsers = useMemo(() => users.filter((u) => u.active !== false), [users]);

  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  const presentCount = items.filter((it) => it.check_in).length;

  // --- Admin attendance edit dialog state ---
  const [editing, setEditing] = useState(null); // {rec, user, ci, co}
  const isAdmin = currentUser?.role === "Admin";
  const openEdit = (u, rec) => {
    setEditing({
      user: u,
      rec,
      ci: rec?.check_in ? toLocalInputValue(rec.check_in) : "",
      co: rec?.check_out ? toLocalInputValue(rec.check_out) : "",
      note: "",
    });
  };
  const saveEdit = async () => {
    if (!editing?.rec) return;
    try {
      const payload = {
        check_in: editing.ci ? new Date(editing.ci).toISOString() : null,
        check_out: editing.co ? new Date(editing.co).toISOString() : null,
        note: editing.note || undefined,
      };
      await api.patch(`/attendance/${editing.rec.id}`, payload);
      toast.success("Attendance updated");
      setEditing(null);
      // Re-fetch
      const r = await api.get("/attendance/all", { params: { date_str: date } });
      setItems(r.data || []);
    } catch (e) {
      toast.error(parseApiError(e, "Failed"));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <div className="gc-label mb-1">Date</div>
          <DatePicker value={date} onChange={(v) => setDate(v || date)} className="w-44" allowClear={false} testId="input-att-date" />
        </div>
        <Button variant="outline" className="rounded-sm" onClick={() => setDate(todayIso())} data-testid="btn-today">Today</Button>
        <div className="ml-auto flex items-center gap-3 text-xs">
          <div className="inline-flex items-center gap-1.5"><Users size={12} className="text-slate-400" /> <span className="font-mono-data">{allUsers.length}</span> staff</div>
          <div className="inline-flex items-center gap-1.5"><Clock size={12} className="text-green-700" /> <span className="font-mono-data">{presentCount}</span> present</div>
        </div>
      </div>

      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2.5">Employee</th>
              <th className="text-left px-4 py-2.5">Role</th>
              <th className="text-left px-4 py-2.5">Check In</th>
              <th className="text-left px-4 py-2.5">Check Out</th>
              <th className="text-left px-4 py-2.5">Duration</th>
              <th className="text-left px-4 py-2.5">Status</th>
              {isAdmin && <th className="text-right px-4 py-2.5 w-16">Edit</th>}
            </tr>
          </thead>
          <tbody>
            {allUsers.map((u) => {
              const rec = indexByUser[u.user_id];
              const ci = rec?.check_in;
              const co = rec?.check_out;
              let duration = "—";
              if (ci) {
                const start = new Date(ci);
                const end = co ? new Date(co) : new Date();
                const mins = Math.max(0, Math.floor((end - start) / 60000));
                const h = Math.floor(mins / 60);
                const m = mins % 60;
                duration = `${h}h ${m}m${co ? "" : " (open)"}`;
              }
              const status = ci ? (co ? "Present" : "Working") : "Absent";
              const inAddr = rec?.check_in_address || (rec?.check_in_lat && rec?.check_in_lng ? `${rec.check_in_lat.toFixed(4)}, ${rec.check_in_lng.toFixed(4)}` : null);
              const outAddr = rec?.check_out_address || (rec?.check_out_lat && rec?.check_out_lng ? `${rec.check_out_lat.toFixed(4)}, ${rec.check_out_lng.toFixed(4)}` : null);
              const mapsLink = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;
              const lateMins = rec?.check_in_late_minutes || 0;
              const remote = rec?.check_in_remote;
              return (
                <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`att-row-${u.user_id}`}>
                  <td className="px-4 py-2.5 font-medium">{u.name}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{u.role}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-mono-data text-xs">{fmtTime(ci)}</span>
                      {lateMins > 0 && (
                        <span className="px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 text-[9px] font-semibold" data-testid={`late-${u.user_id}`}>
                          Late {lateMins}m
                        </span>
                      )}
                      {remote && ci && (
                        <span className="px-1.5 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[9px] font-semibold" data-testid={`remote-${u.user_id}`}>
                          Remote
                        </span>
                      )}
                    </div>
                    {inAddr && rec?.check_in_lat && (
                      <a href={mapsLink(rec.check_in_lat, rec.check_in_lng)} target="_blank" rel="noreferrer" className="block mt-0.5 text-[10px] text-[#0a2540] hover:underline max-w-[260px] truncate" title={inAddr} data-testid={`geo-in-${u.user_id}`}>
                        📍 {inAddr}
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-mono-data text-xs">{fmtTime(co)}</div>
                    {outAddr && rec?.check_out_lat && (
                      <a href={mapsLink(rec.check_out_lat, rec.check_out_lng)} target="_blank" rel="noreferrer" className="block mt-0.5 text-[10px] text-[#0a2540] hover:underline max-w-[260px] truncate" title={outAddr} data-testid={`geo-out-${u.user_id}`}>
                        📍 {outAddr}
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono-data text-xs">{duration}</td>
                  <td className="px-4 py-2.5">
                    <span className={`status-pill ${ci ? (co ? "status-completed" : "status-ongoing") : "status-cancelled"}`}>{status}</span>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-2.5 text-right">
                      {rec ? (
                        <Button
                          size="sm" variant="ghost" className="h-7 w-7 p-0"
                          onClick={() => openEdit(u, rec)}
                          title="Edit check-in / out (Admin)"
                          data-testid={`btn-edit-att-${u.user_id}`}
                        >
                          <Pencil size={12} />
                        </Button>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {allUsers.length === 0 && <tr><td colSpan={isAdmin ? 7 : 6} className="px-4 py-10 text-center text-xs text-slate-400">No staff</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Admin attendance-edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="dialog-edit-attendance">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>
              Edit attendance — {editing?.user?.name}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 text-xs">
              <div className="text-[11px] text-slate-500">
                Date: <span className="font-mono-data text-[#0a2540]">{editing.rec?.date}</span>
              </div>
              <div>
                <Label className="text-xs">Check-in</Label>
                <Input
                  type="datetime-local"
                  value={editing.ci}
                  onChange={(e) => setEditing({ ...editing, ci: e.target.value })}
                  className="mt-1 rounded-sm font-mono-data"
                  data-testid="input-edit-checkin"
                />
              </div>
              <div>
                <Label className="text-xs">Check-out</Label>
                <Input
                  type="datetime-local"
                  value={editing.co}
                  onChange={(e) => setEditing({ ...editing, co: e.target.value })}
                  className="mt-1 rounded-sm font-mono-data"
                  data-testid="input-edit-checkout"
                />
                <div className="mt-1 text-[10px] text-slate-400">
                  Leave check-out blank if the employee genuinely didn't check out.
                </div>
              </div>
              <div>
                <Label className="text-xs">Note (audit trail)</Label>
                <Input
                  value={editing.note}
                  onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                  className="mt-1 rounded-sm"
                  placeholder="e.g. Corrected accidental early checkout per email"
                  data-testid="input-edit-note"
                />
              </div>
              {(editing.rec?.edit_history?.length || 0) > 0 && (
                <details className="text-[10px] text-slate-500">
                  <summary className="cursor-pointer">Edit history ({editing.rec.edit_history.length})</summary>
                  <ul className="mt-1 space-y-1">
                    {editing.rec.edit_history.map((h, i) => (
                      <li key={h.edited_at || `edit-${i}`} className="font-mono-data">
                        {h.edited_at?.slice(0, 16).replace("T", " ")} · by {h.editor_name}
                        {h.note ? ` — "${h.note}"` : ""}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={saveEdit} data-testid="btn-save-attendance">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
